import { html, render, nothing, type TemplateResult } from 'lit-html';
import styles from './pk-webhook-panel.css?inline';
import { withCsrf } from '../../lib/csrf.js';

type PanelState = 'loading' | 'ok' | 'error';

/**
 * `<pk-webhook-panel>` — webhook URL provisioning per system.
 *
 * Attributes:
 *   `system-id`  (REQUIRED)
 *   `tenant-id`  (REQUIRED)
 *   `api-base`   (default `/portal/api/al`)
 *
 * Events:
 *   `pk-webhook-generated` — detail `{ url }`.
 *   `pk-webhook-removed`   — no detail.
 *   `pk-webhook-rotated`   — detail `{ url }`.
 */
export class PkWebhookPanel extends HTMLElement {
    static readonly tagName = 'pk-webhook-panel';

    static get observedAttributes(): string[] {
        return ['system-id', 'tenant-id', 'api-base'];
    }

    private readonly _root: ShadowRoot;

    private _state: PanelState   = 'loading';
    private _url: string | null  = null;
    private _secret: string      = '';
    private _errorMsg: string    = '';
    private _busy: boolean       = false;

    constructor() {
        super();
        this._root = this.attachShadow({ mode: 'open' });
    }

    get systemId(): string { return this.getAttribute('system-id') ?? ''; }
    get tenantId(): string { return this.getAttribute('tenant-id') ?? ''; }
    get apiBase(): string  { return this.getAttribute('api-base') ?? '/portal/api/al'; }

    connectedCallback(): void {
        this._fetch();
    }

    attributeChangedCallback(name: string, oldVal: string | null, newVal: string | null): void {
        // Skip the initial parse-time attribute callbacks — connectedCallback
        // owns the first fetch. Only refetch on real post-connection changes.
        if (oldVal !== newVal && this.isConnected) {
            this._fetch();
        }
    }

    // ── Network ──────────────────────────────────────────────────────────────

    private get _endpoint(): string {
        return `${this.apiBase}/systems/${encodeURIComponent(this.systemId)}/webhook`;
    }

    private async _fetch(): Promise<void> {
        if (!this.systemId) return;
        this._state = 'loading';
        this._doRender();

        try {
            const resp = await fetch(this._endpoint, withCsrf());
            if (resp.status === 404) {
                // No webhook configured yet — valid not-configured state.
                this._url   = null;
                this._state = 'ok';
            } else if (resp.ok) {
                const data = await resp.json() as { url?: string };
                this._url   = data.url ?? null;
                this._state = 'ok';
            } else {
                throw new Error(`HTTP ${resp.status}`);
            }
        } catch (err) {
            this._state    = 'error';
            this._errorMsg = err instanceof Error ? err.message : String(err);
        }
        this._doRender();
    }

    private async _generate(): Promise<void> {
        if (this._busy) return;
        this._busy = true;
        this._doRender();

        try {
            const body: Record<string, string> = {};
            if (this._secret.trim()) body['secret'] = this._secret.trim();

            const resp = await fetch(this._endpoint, withCsrf({
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body),
            }));
            if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
            const data = await resp.json() as { url?: string };
            this._url    = data.url ?? null;
            this._secret = '';
            this.dispatchEvent(new CustomEvent('pk-webhook-generated', {
                bubbles: true, composed: true,
                detail: { url: this._url },
            }));
        } catch (err) {
            this._errorMsg = err instanceof Error ? err.message : String(err);
        } finally {
            this._busy = false;
            this._doRender();
        }
    }

    private async _rotate(): Promise<void> {
        if (this._busy) return;
        if (!confirm('Rotate the webhook URL? The old URL will stop working immediately.')) return;
        this._busy = true;
        this._doRender();

        try {
            const resp = await fetch(this._endpoint + '/rotate', withCsrf({
                method: 'POST',
            }));
            if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
            const data = await resp.json() as { url?: string };
            this._url = data.url ?? null;
            this.dispatchEvent(new CustomEvent('pk-webhook-rotated', {
                bubbles: true, composed: true,
                detail: { url: this._url },
            }));
        } catch (err) {
            this._errorMsg = err instanceof Error ? err.message : String(err);
        } finally {
            this._busy = false;
            this._doRender();
        }
    }

    private async _remove(): Promise<void> {
        if (this._busy) return;
        if (!confirm('Remove the webhook URL? Messages will no longer be forwarded.')) return;
        this._busy = true;
        this._doRender();

        try {
            const resp = await fetch(this._endpoint, withCsrf({
                method: 'DELETE',
            }));
            if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
            this._url = null;
            this.dispatchEvent(new CustomEvent('pk-webhook-removed', {
                bubbles: true, composed: true,
            }));
        } catch (err) {
            this._errorMsg = err instanceof Error ? err.message : String(err);
        } finally {
            this._busy = false;
            this._doRender();
        }
    }

    private async _copyUrl(): Promise<void> {
        if (!this._url) return;
        await navigator.clipboard.writeText(this._url);
    }

    // ── Templates ──────────────────────────────────────────────────────────

    private _notConfiguredTpl(): TemplateResult {
        return html`
            <div class="not-configured">
                <p>No webhook URL configured. Generate one below — messages will be forwarded to that URL.</p>
                <div class="field-group">
                    <label class="field-label" for="wh-secret">Signing secret (optional)</label>
                    <input id="wh-secret"
                        class="secret-input"
                        type="password"
                        placeholder="Leave blank for unsigned webhooks"
                        autocomplete="new-password"
                        .value=${this._secret}
                        @input=${(e: Event) => { this._secret = (e.target as HTMLInputElement).value; }} />
                </div>
                <div class="action-row">
                    <button class="btn btn-primary"
                        type="button"
                        ?disabled=${this._busy}
                        aria-busy=${this._busy ? 'true' : 'false'}
                        @click=${() => this._generate()}>
                        ${this._busy ? 'Generating…' : 'Generate URL'}
                    </button>
                </div>
            </div>
        `;
    }

    private _configuredTpl(): TemplateResult {
        return html`
            <div>
                <div class="url-row">
                    <input class="url-input"
                        type="text"
                        readonly
                        aria-label="Webhook URL"
                        .value=${this._url ?? ''} />
                    <button class="btn btn-secondary"
                        type="button"
                        aria-label="Copy webhook URL"
                        @click=${() => this._copyUrl()}>
                        Copy
                    </button>
                </div>
                <div class="action-row" style="margin-top: var(--pk-space-4, 0.85rem);">
                    <button class="btn btn-secondary"
                        type="button"
                        ?disabled=${this._busy}
                        @click=${() => this._rotate()}>
                        ${this._busy ? 'Rotating…' : 'Rotate URL'}
                    </button>
                    <button class="btn btn-danger"
                        type="button"
                        ?disabled=${this._busy}
                        @click=${() => this._remove()}>
                        ${this._busy ? 'Removing…' : 'Remove'}
                    </button>
                </div>
            </div>
        `;
    }

    private template(): TemplateResult {
        const bodyContent = (): TemplateResult => {
            if (this._state === 'loading') {
                return html`<div class="loading" aria-busy="true">Loading…</div>`;
            }
            if (this._state === 'error') {
                return html`<div class="alert" role="alert">${this._errorMsg}</div>`;
            }
            return this._url ? this._configuredTpl() : this._notConfiguredTpl();
        };

        return html`
            <style>${styles}</style>
            <div class="panel">
                <div class="header">
                    <h2 class="header-title">Webhook — ${this.systemId || '—'}</h2>
                </div>
                <div class="body">
                    ${this._errorMsg && this._state === 'ok' ? html`
                        <div class="alert" role="alert">${this._errorMsg}</div>
                    ` : nothing}
                    ${bodyContent()}
                </div>
            </div>
        `;
    }

    private _doRender(): void {
        render(this.template(), this._root);
    }
}

if (!customElements.get(PkWebhookPanel.tagName)) {
    customElements.define(PkWebhookPanel.tagName, PkWebhookPanel);
}

declare global {
    interface HTMLElementTagNameMap {
        'pk-webhook-panel': PkWebhookPanel;
    }
}
